import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Zap } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';

import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { LoginArea } from '@/components/auth/LoginArea';
import NotFound from '@/pages/NotFound';
import { PowMiningBar } from '@/components/stashstr/PowMiningBar';
import { ZapButton } from '@/components/stashstr/ZapButton';
import { PaytoButtons } from '@/components/stashstr/PaytoButtons';
import { Poll } from '@/components/stashstr/Poll';
import { VoteArrows } from '@/components/stashstr/VoteArrows';
import { AuthorLink } from '@/components/stashstr/AuthorLink';
import { BackButton } from '@/components/stashstr/BackButton';
import { UnprovenImage } from '@/components/stashstr/UnprovenImage';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useItem, useItemComments, type CommentEntry } from '@/hooks/useItem';
import { useAuthors } from '@/hooks/useAuthors';
import { useZapTotal } from '@/hooks/useZapTotal';
import { mineThenPublish } from '@/lib/publishPow';
import {
  COMMENT_MAX,
  COMMENT_POW,
  HIDE_UNPROVEN_DEFAULT,
  POST_POW,
  RELAYS,
  linkifyParts,
  getImetas,
  getLink,
  getPollId,
  getTerritories,
  getTitle,
  readHideUnproven,
  sanitizeUrl,
  timeAgo,
} from '@/lib/stashstr';
import { verifyPow } from '@/lib/pow';

export default function ItemPage() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  // Decode: accept nevent/note only
  let eventId: string | undefined;
  let eventPubkey: string | undefined;
  if (identifier) {
    try {
      const decoded = nip19.decode(identifier);
      if (decoded.type === 'nevent') {
        eventId = decoded.data.id;
        eventPubkey = decoded.data.author;
      } else if (decoded.type === 'note') {
        eventId = decoded.data;
      }
    } catch {
      eventId = undefined;
    }
  }

  if (!eventId) return <NotFound />;

  return <ItemContent rootId={eventId} rootPubkey={eventPubkey} />;
}

function ItemContent({ rootId, rootPubkey }: { rootId: string; rootPubkey?: string }) {
  const { data: event, isLoading, isError } = useItem(rootId);
  // Shared cache with ZapButton's internal useZapTotal (same queryKey) — no extra round-trip.
  const zapTotalData = useZapTotal(event);
  const eventUnverifiedTotal = zapTotalData.data?.unverifiedTotal ?? 0;
  const eventUnverifiedCount = zapTotalData.data?.unverifiedCount ?? 0;

  // NOTE: all hooks above run unconditionally. Rendering of the loaded body
  // is deferred to ItemBody so its hooks (useAuthors) keep a stable order.

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-6 w-3/4 bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
        <div className="h-3 w-1/2 bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
        <div className="h-24 w-full bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
      </div>
    );
  }

  if (isError || !event) return <NotFound />;

  return (
    <>
      <BackButton />
      <ItemBodyWrapper
        event={event}
        rootId={rootId}
        unverifiedTotal={eventUnverifiedTotal}
        unverifiedCount={eventUnverifiedCount}
      />
    </>
  );
}

function ItemBody({
  event,
  rootId,
  unverifiedTotal,
  unverifiedCount,
}: {
  event: NostrEvent;
  rootId: string;
  unverifiedTotal: number;
  unverifiedCount: number;
}) {
  const title = getTitle(event);
  const link = getLink(event);
  const territories = getTerritories(event);
  const pollId = getPollId(event) ?? undefined;
  const images = getImetas(event);
  // event.pubkey is ground truth (signature); the nevent author hint is
  // routing-only and can be stale — never use it for identity.
  const authorKey = event.pubkey;
  const postPowValid = verifyPow(event, POST_POW);
  const authorMap = useAuthors([authorKey]).data ?? new Map<string, NostrMetadata>();
  const postAuthorMeta = authorMap.get(authorKey);
  const postAuthorName =
    postAuthorMeta?.display_name || postAuthorMeta?.name || authorKey.slice(0, 8);

  return (
    <article className="rounded-md border border-gray-200 dark:border-[#2b2f34] bg-[#f6f7f9] dark:bg-[#1e2023] p-4">
      <div className="flex gap-3">
        <VoteArrows targetId={event.id} targetKind="11" targetPubkey={authorKey} />
        <div className="flex-1 min-w-0">
          <h1 className="text-[20px] font-bold text-gray-900 dark:text-gray-100 leading-tight">
            {title}
            {!postPowValid && (
              <Badge
                variant="outline"
                className="text-[10px] border-amber-400 text-amber-600 dark:text-amber-400 ml-2 align-middle"
              >
                Unproven
              </Badge>
            )}
          </h1>

          <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-1.5 items-center">
            <span className="inline-flex items-center gap-0.5">
              <Zap className="size-3 text-[#f97316] dark:text-[#fb923c]" fill="currentColor" />
              <ZapButton event={event} />
            </span>
            {unverifiedCount > 0 && (
              <span
                className="text-[11px] text-amber-600 dark:text-amber-400"
                title="Zaps from receipts without a recipient tag — not counted toward ranking."
              >
                (+{unverifiedTotal} unverified)
              </span>
            )}
            <span aria-hidden="true">\</span>
            <span>{timeAgo(event.created_at)}</span>
            <AuthorLink pubkey={authorKey} name={postAuthorName} />
            {territories.map((t) => (
              <Link
                key={t}
                to={`/t/${t}`}
                className="hover:text-gray-800 dark:hover:text-gray-200"
              >
                ~{t}
              </Link>
            ))}
          </div>

          {link && (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="block mt-3 text-[13px] text-[#1a56db] dark:text-[#6ea8fe] hover:underline break-all"
            >
              {link}
            </a>
          )}

          {event.content.trim() && (
            <div className="mt-3 text-[14px] text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
              <Linkified
                text={postPowValid ? stripImageUrls(event.content, images) : event.content}
              />
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              {postPowValid
                ? images.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt=""
                        loading="lazy"
                        className="w-full h-auto object-cover rounded border border-gray-200 dark:border-[#2a2d30]"
                      />
                    </a>
                  ))
                : images.map((url) => (
                    <UnprovenImage key={url} url={url} variant="gallery" className="w-full aspect-video" />
                  ))}
            </div>
          )}

          {pollId && <Poll pollId={pollId} />}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <PaytoButtons pubkey={authorKey} />
          </div>
        </div>
      </div>
    </article>
  );
}

function ItemBodyWrapper(props: {
  event: NostrEvent;
  rootId: string;
  unverifiedTotal: number;
  unverifiedCount: number;
}) {
  return (
    <>
      <ItemBody {...props} />
      <CommentThread rootId={props.rootId} rootPubkey={props.event.pubkey} />
    </>
  );
}

/** Drops lines that exactly match an image URL already shown in the gallery. */
function stripImageUrls(text: string, imageUrls: string[]): string {
  if (imageUrls.length === 0) return text;
  // Normalize both sides through sanitizeUrl so trailing-slash differences
  // (e.g. https://x.com vs https://x.com/) don't defeat the match.
  const norm = (line: string): string => sanitizeUrl(line.trim()) ?? line.trim();
  const imageSet = new Set(imageUrls.map(norm));
  return text
    .split('\n')
    .filter((line) => !imageSet.has(norm(line)))
    .join('\n');
}

/** Renders plaintext with safe linkified URLs. */
function Linkified({ text }: { text: string }) {
  return (
    <>
      {linkifyParts(text).map((part, i) =>
        part.type === 'url' ? (
          <a
            key={i}
            href={part.href}
            target="_blank"
            rel="noreferrer"
            className="text-[#1a56db] dark:text-[#6ea8fe] hover:underline break-all"
          >
            {part.value}
          </a>
        ) : (
          <span key={i}>{part.value}</span>
        ),
      )}
    </>
  );
}

function CommentThread({ rootId, rootPubkey }: { rootId: string; rootPubkey: string }) {
  const { user } = useCurrentUser();
  const { data, isLoading } = useItemComments(rootId);
  const [hideUnproven, setHideUnproven] = useState(() => {
    try {
      return readHideUnproven();
    } catch {
      return HIDE_UNPROVEN_DEFAULT;
    }
  });

  const entries = data?.entries ?? [];
  const proven = entries.filter((e) => e.powValid);
  const unproven = entries.filter((e) => !e.powValid);
  const hiddenCount = unproven.length;

  // Batched author lookup: ONE kind-0 query for every comment author.
  const authorMap = useAuthors(entries.map((e) => e.event.pubkey)).data ?? new Map<string, NostrMetadata>();

  return (
    <section className="mt-6" aria-label="Comments">
      <h2 className="text-[14px] font-semibold text-gray-700 dark:text-gray-300 mb-2">
        {entries.length} {entries.length === 1 ? 'comment' : 'comments'}
      </h2>

      {user ? (
        <CommentForm rootId={rootId} rootPubkey={rootPubkey} />
      ) : (
        <Card className="border-dashed mb-4">
          <CardContent className="py-4 text-center text-[13px] text-muted-foreground flex flex-col items-center gap-2">
            <span>Log in to comment.</span>
            <LoginArea className="max-w-40" />
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-2 mt-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-[#232528] animate-pulse rounded" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-[#2a2d30]">
          {(hideUnproven ? proven : entries).map((entry) => (
            <CommentRow
              key={entry.event.id}
              entry={entry}
              authorMeta={authorMap.get(entry.event.pubkey)}
            />
          ))}

          {hideUnproven && hiddenCount > 0 && (
            <div className="py-2">
              <button
                type="button"
                onClick={() => setHideUnproven(false)}
                className="text-[12px] text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                + {hiddenCount} hidden (unproven). Show
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CommentRow({
  entry,
  authorMeta,
}: {
  entry: CommentEntry;
  authorMeta: NostrMetadata | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const { event, powValid } = entry;
  const name =
    authorMeta?.display_name || authorMeta?.name || event.pubkey.slice(0, 6);
  const picture = authorMeta?.picture;

  if (!powValid && !expanded) {
    return (
      <div className="py-2">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[12px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#1a1c1e] border border-gray-200 dark:border-[#2a2d30] rounded px-2 py-1 hover:bg-gray-100 dark:hover:bg-[#232528]"
        >
          Show unproven comment — {COMMENT_POW}-bit PoW missing. Click to expand.
        </button>
      </div>
    );
  }

  return (
    <div className={`py-3 ${powValid ? '' : 'opacity-70'}`}>
      <div className="flex items-start gap-2">
        <Avatar className="w-6 h-6 mt-0.5">
          <AvatarImage src={sanitizeUrl(picture)} alt="" />
          <AvatarFallback className="text-[10px] bg-gray-100 dark:bg-[#232528] text-gray-600 dark:text-gray-300">
            {name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] text-gray-500 dark:text-gray-400 flex items-center gap-2 flex-wrap">
            <AuthorLink pubkey={event.pubkey} name={name} />
            <span>{timeAgo(event.created_at)}</span>
            {!powValid && (
              <Badge
                variant="outline"
                className="text-[10px] border-amber-400 text-amber-600 dark:text-amber-400"
              >
                Unproven
              </Badge>
            )}
          </div>
          <div className="text-[13px] text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words mt-0.5">
            <Linkified text={event.content} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentForm({ rootId, rootPubkey }: { rootId: string; rootPubkey: string }) {
  const { user } = useCurrentUser();
  const [content, setContent] = useState('');
  const [mining, setMining] = useState(false);
  const [tries, setTries] = useState(0);
  const [miningStart, setMiningStart] = useState(0);
  const cancelRef = useRef(false);
  const publish = useNostrPublish();
  const queryClient = useQueryClient();

  const submit = async () => {
    const trimmed = content.trim();
    if (!trimmed || trimmed.length > COMMENT_MAX || mining || !user) return;
    setMining(true);
    cancelRef.current = false;
    setTries(0);
    setMiningStart(Date.now());
    try {
      await mineThenPublish({
        userPubkey: user.pubkey,
        publish: publish.mutateAsync,
        kind: 1111,
        content: trimmed,
        // Dual-case reference tags: relays index lowercase only; uppercase
        // kept for NIP-22-style clients that read E/K/P.
        tags: [
          ['K', '11'],
          ['k', '11'],
          ['E', rootId, RELAYS[0], rootPubkey],
          ['e', rootId, RELAYS[0], rootPubkey],
          ['P', rootPubkey],
          ['p', rootPubkey],
        ],
        difficulty: COMMENT_POW,
        onProgress: setTries,
        shouldCancelRef: cancelRef,
      });
      // Refresh comment list + feed comment counts immediately
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'comments', rootId] });
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'feed'] });
      setContent('');
    } catch {
      // cancelled or failed — surfaced via mining bar reset
    } finally {
      setMining(false);
      setTries(0);
    }
  };

  return (
    <div className="mb-4 space-y-2">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write a comment…"
        maxLength={COMMENT_MAX}
        className="text-[13px] min-h-[70px]"
        aria-label="Comment"
      />
      {mining ? (
        <PowMiningBar
          tries={tries}
          difficulty={COMMENT_POW}
          startedAt={miningStart}
          onCancel={() => {
            cancelRef.current = true;
          }}
        />
      ) : (
        <Button
          type="button"
          onClick={submit}
          disabled={!content.trim() || publish.isPending}
          className="h-8 px-3 text-[13px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
        >
          Mine + Post (20-bit, ~1–2 min)
        </Button>
      )}
    </div>
  );
}
